/**
 * O oferta din meniu: card in grila + fisa de produs in foaie.
 *
 * Pretul afisat e cel al ofertei (ADR-010: pretul apartine platformei, nu
 * POS-ului), asa cum vine de la core — nu se recalculeaza aici. Singurul lucru
 * pe care il aduna cardul sunt diferentele optiunilor alese (`priceDelta`), care
 * sunt tot preturi publicate, nu calcule proprii.
 *
 * ADR-024, doua cerinte care se vad direct pe ecran:
 *  - pozele produsului (`images[]`, ordonate), nu doar prima;
 *  - grupurile de optiuni, cu pretul actualizat la fiecare alegere. Un grup
 *    OBLIGATORIU neales blocheaza adaugarea in cos — altfel "pizza cu blat
 *    pufos" ajunge la bucatarie ca "pizza", iar bonul si consumul sunt gresite.
 *
 * Fisa: poza pana la margine, titlu si pret, descriere, optiunile ca randuri
 * mari de bifat, mentiuni pentru bucatarie, iar jos — LIPITA — cantitatea si
 * butonul cu pretul total al alegerii. E mecanica foii de produs din
 * aplicatiile de livrare: ce costa se vede in acelasi loc cu ce se apasa.
 *
 * Structura cardului foloseste "legatura intinsa": titlul e butonul, iar
 * `::after` al lui acopera tot cardul. Asa exista O SINGURA tinta mare de
 * atingere pentru "deschide produsul", fara sa imbrici butonul de adaugare
 * rapida in ea — imbricarea a doua butoane e HTML nevalid.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  MAX_QTY_PER_LINE,
  type MoneyMinor,
  type OfferDto,
  type OfferImageDto,
} from "@deliveryapp/shared";

import { IconHeart, IconPlus, IconWarning } from "./icons";
import { ImageFallback, ResponsiveImage } from "./Media";
import { Modal } from "./Modal";
import {
  formatNetWeight,
  friendlyDietTag,
  nutritionBasisLabel,
  nutritionRows,
  offerCustomerUnitPrice,
  offerPromotion,
} from "./offer-merchandising";
import {
  cartLineIdFor,
  readOfferOptions,
  resolveSelection,
  optionKeepsFulfillmentPossible,
  projectOptionGroupsForSelection,
  selectedIdsFor,
  selectionBlockers,
  selectionFitsFulfillmentVariant,
  selectionAfterOptionToggle,
  selectionHasFinanciallyValidCompletion,
  selectionPriceDelta,
  unsatisfiableGroups,
  type OptionSelection,
  type ResolvedOption,
} from "./offer-options";
import { Badge, Button, ChoiceRow, Price, Stepper, TextArea } from "./ui";

const UNAVAILABLE_REASONS: Record<NonNullable<OfferDto["unavailableReason"]>, string> = {
  sold_out: "S-a terminat",
  out_of_schedule: "În afara programului",
  no_vendor: "Indisponibil la adresa ta",
};

/**
 * Pozele produsului, in ordinea din meniu. Lipsa pozei NU e eroare (ADR-024).
 * Cand `images` LIPSESTE (un core mai vechi), singura sursa ramane `imageUrl`.
 */
function orderedImages(offer: OfferDto): OfferImageDto[] {
  const raw = (offer as unknown as Record<string, unknown>).images;
  if (!Array.isArray(raw)) {
    const fallback = offer.imageUrl;
    return typeof fallback === "string" && fallback.length > 0 ? [{ url: fallback, sortOrder: 0 }] : [];
  }
  const images: OfferImageDto[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const image = entry as Record<string, unknown>;
    if (typeof image.url !== "string" || image.url.length === 0) continue;
    images.push({
      url: image.url,
      sortOrder: typeof image.sortOrder === "number" ? image.sortOrder : images.length,
      width: typeof image.width === "number" ? image.width : null,
      height: typeof image.height === "number" ? image.height : null,
    });
  }
  return images.sort((a, b) => a.sortOrder - b.sortOrder);
}

export interface OfferAddPayload {
  lineId: string;
  unitPriceGross: MoneyMinor;
  compareAtUnitPrice: MoneyMinor | null;
  options: ResolvedOption[];
  /** Poza pusa pe linia de cos: prima dintre cele pe care cardul chiar le-a putut afisa. */
  imageUrl: string | null;
  /** Cantitatea FINALA, aratata clientului impreuna cu ce plateste. */
  qty: number;
  /** Mentiunile pentru bucatarie, scrise in fisa. Absent = fara. */
  notes?: string | null;
}

export function OfferCard({
  offer,
  qtyInCart,
  onAdd,
  onDecrease,
  favorite = false,
  onToggleFavorite,
  /** Prima linie de produse se incarca devreme; restul sunt lenese. */
  priority = false,
  /** Bucataria n-a fost inca aleasa (fara adresa): fisa o spune, nu blocheaza. */
  addressPending = false,
}: {
  offer: OfferDto;
  qtyInCart: number;
  onAdd: (payload: OfferAddPayload) => void;
  onDecrease: () => void;
  favorite?: boolean;
  onToggleFavorite?: () => void;
  priority?: boolean;
  addressPending?: boolean;
}) {
  const options = useMemo(() => readOfferOptions(offer), [offer]);
  const images = useMemo(() => orderedImages(offer), [offer]);
  const minQty = Number.isSafeInteger(offer.minQty) ? Math.max(1, offer.minQty) : 1;
  const [activeImage, setActiveImage] = useState(0);
  const [selection, setSelection] = useState<OptionSelection>({});
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(minQty);
  const [notes, setNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [showBlockers, setShowBlockers] = useState(false);
  /**
   * Unde a fost dus clientul la „Adauga" incomplet: grupul lipsa sau, fara grup
   * (combinatie/pret), lista de motive (`groupId: null`). `n` reporneste animatia.
   */
  const [nudge, setNudge] = useState<{ groupId: string | null; n: number } | null>(null);
  const groupRefs = useRef(new Map<string, HTMLFieldSetElement>());
  const blockersRef = useRef<HTMLUListElement>(null);

  const displayGroups = useMemo(
    () => projectOptionGroupsForSelection(options.groups, options.variants, selection),
    [options.groups, options.variants, selection],
  );
  const hasOptions = displayGroups.length > 0;
  const priceDelta = selectionPriceDelta(displayGroups, selection);
  const unitPrice = offerCustomerUnitPrice(offer) + priceDelta;
  const selectedAddedAllergens = useMemo(() => {
    const parent = new Set(offer.allergens.map((value) => value.toLocaleLowerCase("ro")));
    return [...new Set(displayGroups.flatMap((group) =>
      group.options.flatMap((option) =>
        selectedIdsFor(selection, group.id).includes(option.id) ? option.allergens : [],
      ),
    ))].filter((value) => !parent.has(value.toLocaleLowerCase("ro")));
  }, [displayGroups, offer.allergens, selection]);
  const selectedOptions = useMemo(
    () => displayGroups.flatMap((group) =>
      group.options.filter((option) => selectedIdsFor(selection, group.id).includes(option.id)),
    ),
    [displayGroups, selection],
  );
  const effectiveDietTags = useMemo(
    () => offer.dietTags.filter((tag) =>
      selectedOptions.every((option) => option.foodImpact === "no_impact" || option.dietTags.includes(tag)),
    ),
    [offer.dietTags, selectedOptions],
  );
  const removedDietTags = offer.dietTags.filter((tag) => !effectiveDietTags.includes(tag));
  const blockers = [
    ...selectionBlockers(displayGroups, selection),
    ...(unitPrice < 0 ? ["Combinația aleasă ar produce un preț invalid. Schimbă opțiunile."] : []),
    ...(selectionFitsFulfillmentVariant(options.variants, selection, true)
      ? []
      : ["Combinația aleasă nu poate fi pregătită integral de același restaurant. Ajustează opțiunile marcate."]),
  ];
  const ceiling = Math.min(offer.maxQty ?? MAX_QTY_PER_LINE, MAX_QTY_PER_LINE);
  const unsatisfiable = [
    ...unsatisfiableGroups(displayGroups),
    ...(minQty > ceiling
      ? [
          ceiling <= 0
            ? "Produsul nu poate fi comandat în acest moment."
            : `Produsul se comandă de la ${minQty} buc., dar sunt disponibile cel mult ${ceiling}.`,
        ]
      : []),
    ...(!selectionHasFinanciallyValidCompletion(options.groups, options.variants, {}, offerCustomerUnitPrice(offer))
      ? ["Nicio configurație disponibilă nu are un preț valid în acest moment."]
      : []),
  ];
  const orderable = !options.unreadable && unsatisfiable.length === 0;

  // Indisponibil FARA motiv declarat ramane indisponibil.
  const unavailable = !offer.available;
  const unavailableLabel: string | null = !unavailable
    ? null
    : offer.unavailableReason === null || offer.unavailableReason === undefined
      ? "Indisponibil"
      : (UNAVAILABLE_REASONS[offer.unavailableReason] ?? "Indisponibil");

  const canAdd = !unavailable && orderable;
  const room = ceiling - qtyInCart;
  const atMax = room <= 0;
  const mainImage = images[Math.min(activeImage, Math.max(0, images.length - 1))];
  const promotion = offerPromotion(offer);
  const weightLabel = formatNetWeight(offer);
  const declaredNutrition = nutritionRows(offer.nutrition);
  const selectedNutritionDeclarations = selectedOptions.flatMap((option) => {
    const rows = nutritionRows(option.nutrition);
    return rows.length === 0 ? [] : [{ option, rows }];
  });
  const hasDescription = typeof offer.description === "string" && offer.description.length > 0;

  const closeSheet = (): void => {
    setOpen(false);
    setSelection({});
    setShowBlockers(false);
    setNudge(null);
    setQty(minQty);
    setNotes("");
    setNotesOpen(false);
    setActiveImage(0);
  };

  const toggleOption = (groupId: string, optionId: string, maxSelect: number): void => {
    setSelection((current) => {
      const chosen = selectedIdsFor(current, groupId);
      if (maxSelect <= 1) {
        return { ...current, [groupId]: chosen.includes(optionId) ? [] : [optionId] };
      }
      if (chosen.includes(optionId)) {
        return { ...current, [groupId]: chosen.filter((item) => item !== optionId) };
      }
      if (chosen.length >= maxSelect) return current;
      return { ...current, [groupId]: [...chosen, optionId] };
    });
    setShowBlockers(false);
    setNudge(null);
  };

  /**
   * „Adauga" cu alegeri lipsa: clientul e dus la PRIMUL grup incomplet (garnitura
   * de mai sus, nu lista de erori de la capatul fisei, pe care nu o vede), iar
   * eticheta grupului clipeste o data.
   */
  const guideToMissingChoice = (): void => {
    const missing = displayGroups.find(
      (group) =>
        selectedIdsFor(selection, group.id).filter((optionId) =>
          group.options.some((option) => option.id === optionId && option.available),
        ).length < group.minSelect,
    );
    setNudge((current) => ({ groupId: missing?.id ?? null, n: (current?.n ?? 0) + 1 }));
  };

  // Derularea dupa randare: lista de motive apare abia la acest clic. Tinta are
  // `scroll-mt-16`, ca sa nu ajunga sub butonul × plutitor al fisei. Focusul
  // intra pe prima optiune, ca tastatura si cititorul de ecran sa ajunga acolo.
  useEffect(() => {
    if (nudge === null) return;
    const target: HTMLElement | null = nudge.groupId === null ? blockersRef.current : (groupRefs.current.get(nudge.groupId) ?? null);
    if (target === null) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
    target.querySelector<HTMLInputElement>("input:not(:disabled)")?.focus({ preventScroll: true });
  }, [nudge]);

  /** Adaugarea. Fail-closed: un produs ale carui optiuni nu se pot citi nu intra in cos pe nicio cale. */
  const add = (wanted: number): void => {
    if (!canAdd || room <= 0) return;
    if (blockers.length > 0) {
      setShowBlockers(true);
      if (open) guideToMissingChoice();
      setOpen(true);
      return;
    }
    const chosen = resolveSelection(displayGroups, selection);
    onAdd({
      lineId: cartLineIdFor(offer.offerId, chosen),
      unitPriceGross: unitPrice,
      compareAtUnitPrice: promotion === null ? null : ((promotion.compareAtPrice + priceDelta) as MoneyMinor),
      options: chosen,
      imageUrl: images[0]?.url ?? null,
      qty: wanted,
      notes: notes.trim().length > 0 ? notes.trim() : null,
    });
    closeSheet();
  };

  /**
   * Butonul "+" de pe card. Cu optiuni sau cu minim mai mare decat 1 (prima
   * data), deschide fisa — un produs configurabil adaugat dintr-un clic ar intra
   * pe o configuratie pe care clientul nu a ales-o.
   */
  const quickAdd = (): void => {
    if (hasOptions || (minQty > 1 && qtyInCart === 0)) {
      setOpen(true);
      return;
    }
    add(1);
  };

  return (
    <li className="da-card da-defer da-card-link relative isolate flex min-h-[7rem] flex-row overflow-hidden sm:flex-col">
      {/* Pe telefon: miniatura PATRATA de 7rem, cat randul; de la `sm`: poza 4/3
          deasupra textului. Raportul de pe telefon vine din prop (stil inline),
          cel de pe ecran mare il suprascrie clasa cu `!important` — altfel
          `h-full` + 4/3 dadea o poza mai lata decat coloana ei, peste text. */}
      <div className="relative w-28 shrink-0 self-start sm:w-auto sm:self-auto">
        <ResponsiveImage
          src={mainImage?.url ?? null}
          alt=""
          ratio="1/1"
          width={mainImage?.width}
          height={mainImage?.height}
          priority={priority}
          rounded={false}
          className={`sm:!aspect-[4/3] ${unavailable ? "opacity-50" : ""}`}
          fallback={<ImageFallback />}
        />

        {promotion === null || unavailableLabel !== null ? null : (
          <span className="absolute left-2 top-2 rounded-full bg-danger px-2 py-0.5 text-[11px] font-extrabold text-white shadow-sm">
            {promotion.percent === null ? "Ofertă" : `−${promotion.percent}%`}
          </span>
        )}

        {unavailableLabel === null ? null : (
          <span className="absolute left-2 top-2 rounded-control bg-canvas/95 px-2 py-0.5 text-[11px] font-bold text-ink">
            {unavailableLabel}
          </span>
        )}

        {onToggleFavorite === undefined ? null : (
          <button
            type="button"
            onClick={onToggleFavorite}
            aria-label={favorite ? `Scoate ${offer.name} din favorite` : `Adaugă ${offer.name} la favorite`}
            aria-pressed={favorite}
            className={`da-press absolute right-1.5 top-1.5 z-10 hidden h-9 w-9 place-items-center rounded-full bg-canvas/95 shadow-md backdrop-blur sm:grid ${favorite ? "text-danger" : "text-ink"}`}
          >
            <IconHeart size={18} filled={favorite} />
          </button>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1 p-3">
        <h3 className="text-sm font-bold leading-snug text-ink sm:text-base">
          {/* Legatura intinsa: `::after` face din tot cardul o tinta pentru deschiderea fisei. */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="da-clamp-2 text-left after:absolute after:inset-0 after:content-['']"
          >
            {offer.name}
          </button>
        </h3>

        {!hasDescription ? null : <p className="da-clamp-2 text-xs text-ink-muted">{offer.description}</p>}

        {minQty <= 1 ? null : <p className="text-xs font-semibold text-ink-muted">{`min. ${minQty} buc.`}</p>}

        <div className="mt-auto flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pr-12 pt-1.5">
          <Price amount={offerCustomerUnitPrice(offer)} className="text-sm font-extrabold text-ink sm:text-base" />
          {promotion === null ? null : (
            <span className="text-xs text-ink-muted line-through">
              <Price amount={promotion.compareAtPrice} />
            </span>
          )}
          {weightLabel === null ? null : <span className="text-xs text-ink-muted">{weightLabel}</span>}
          {offer.allergens.length === 0 ? null : (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-warning" title={`Alergeni: ${offer.allergens.join(", ")}`}>
              <IconWarning size={12} /> alergeni
            </span>
          )}
        </div>
      </div>

      {/* Actiunea rapida: FRATE cu butonul intins, ridicata peste el prin `z-10`. */}
      {!canAdd ? null : (
        <div className="absolute bottom-2 right-2 z-10">
          {!hasOptions && qtyInCart > 0 ? (
            <Stepper
              size="sm"
              value={qtyInCart}
              name={offer.name}
              min={0}
              max={ceiling}
              onDecrease={onDecrease}
              onIncrease={quickAdd}
              className="shadow-md"
            />
          ) : (
            <button
              type="button"
              onClick={quickAdd}
              disabled={atMax}
              aria-label={
                hasOptions
                  ? `Alege opțiunile pentru ${offer.name}`
                  : minQty > 1 && qtyInCart === 0
                    ? `Adaugă ${offer.name} în coș (minimum ${minQty} buc.)`
                    : `Adaugă ${offer.name} în coș`
              }
              className="da-press grid h-10 w-10 place-items-center rounded-full bg-brand text-brand-contrast shadow-md hover:bg-brand-hover disabled:opacity-40"
            >
              {hasOptions && qtyInCart > 0 ? (
                <span className="text-sm font-extrabold tabular-nums">{qtyInCart}</span>
              ) : (
                <IconPlus size={22} />
              )}
            </button>
          )}
        </div>
      )}

      {/* Fisa se MONTEAZA la deschidere, nu se ascunde: 477 de ferestre construite ca sa fie vazuta una ar costa cat lista. */}
      {!open ? null : (
        <Modal
          open={open}
          onClose={closeSheet}
          title={offer.name}
          hideHeader={mainImage !== undefined}
          flush
          footer={
            unavailableLabel !== null ? (
              <p className="text-center text-sm font-semibold text-ink-muted">{unavailableLabel}</p>
            ) : !orderable ? null : (
              <div className="flex items-center gap-2 sm:gap-3">
                <Stepper
                  size="sm"
                  value={qty}
                  name={offer.name}
                  min={minQty}
                  max={Math.max(minQty, room)}
                  onDecrease={() => setQty((current) => Math.max(minQty, current - 1))}
                  onIncrease={() => setQty((current) => Math.min(current + 1, Math.max(minQty, room)))}
                />
                {/* Eticheta scurta + suma, pe UN rand: „Adauga in cos · 64,00 lei"
                    se rupea pe doua randuri langa stepper, pe telefon. */}
                <Button
                  onClick={() => add(qty)}
                  disabled={atMax}
                  block
                  size="lg"
                  className="whitespace-nowrap"
                  trailing={<Price amount={unitPrice * qty} />}
                >
                  {qtyInCart > 0 && !hasOptions ? "Mai adaugă" : "Adaugă"}
                </Button>
              </div>
            )
          }
        >
          <div className="flex flex-col gap-4">
            {mainImage === undefined ? null : (
              <div className="flex flex-col gap-2">
                <ResponsiveImage
                  src={mainImage.url}
                  alt=""
                  ratio="16/10"
                  width={mainImage.width}
                  height={mainImage.height}
                  sizes="(min-width: 640px) 36rem, 100vw"
                  rounded={false}
                  fallback={<ImageFallback />}
                  priority={false}
                />
                {images.length < 2 ? null : (
                  <div className="da-rail px-4" role="group" aria-label={`Poze pentru ${offer.name}`}>
                    {images.map((image, index) => (
                      <button
                        key={image.url}
                        type="button"
                        onClick={() => setActiveImage(index)}
                        aria-label={`Poza ${index + 1} din ${images.length}`}
                        aria-pressed={index === activeImage}
                        className={[
                          "h-14 w-14 overflow-hidden rounded-control border-2",
                          index === activeImage ? "border-brand" : "border-line",
                        ].join(" ")}
                      >
                        <ResponsiveImage src={image.url} alt="" ratio="1/1" rounded={false} sizes="3.5rem" fallback={<ImageFallback />} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-3 px-4 pb-2">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-extrabold leading-tight text-ink sm:text-2xl">{offer.name}</h2>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Price amount={offerCustomerUnitPrice(offer)} className="text-lg font-extrabold text-ink" />
                  {promotion === null ? null : (
                    <>
                      <Price amount={promotion.compareAtPrice} className="text-sm text-ink-muted line-through" />
                      <Badge tone="promo">{promotion.percent === null ? "Ofertă" : `−${promotion.percent}%`}</Badge>
                    </>
                  )}
                  {weightLabel === null ? null : <span className="text-sm text-ink-muted">· {weightLabel}</span>}
                  {minQty <= 1 ? null : <span className="text-sm font-semibold text-ink-muted">· min. {minQty} buc.</span>}
                </div>
              </div>

              {!hasDescription ? null : <p className="text-sm leading-relaxed text-ink">{offer.description}</p>}

              {addressPending ? (
                <p className="rounded-control bg-surface px-3 py-2 text-xs text-ink-muted">
                  Disponibilitatea și opțiunile se confirmă după ce adaugi adresa. Poți pune produsul în coș și acum.
                </p>
              ) : null}

              {effectiveDietTags.length === 0 ? null : (
                <div className="flex flex-wrap gap-1.5" aria-label="Preferințe alimentare">
                  {effectiveDietTags.map((tag) => (
                    <Badge key={`diet-${tag}`} tone="brand">{friendlyDietTag(tag)}</Badge>
                  ))}
                </div>
              )}
              {removedDietTags.length === 0 ? null : (
                <p role="status" className="rounded-control border border-warning bg-surface px-3 py-2 text-xs text-ink">
                  Alegerile tale nu mai sunt confirmate ca {removedDietTags.map(friendlyDietTag).join(" / ")}.
                </p>
              )}

              {options.unreadable ? (
                <p role="alert" className="text-sm font-medium text-danger">
                  Produsul are opțiuni pe care această versiune a aplicației nu le poate afișa, deci nu poate fi
                  comandat corect. Reîncarcă pagina; dacă mesajul rămâne, comandă telefonic.
                </p>
              ) : unsatisfiable.length === 0 ? null : (
                <div role="alert" className="text-sm font-medium text-danger">
                  <p>Produsul nu poate fi comandat online în acest moment:</p>
                  <ul className="list-disc pl-5 font-normal">
                    {unsatisfiable.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {unavailable || !orderable || !hasOptions
                ? null
                : displayGroups.map((group) => {
                    const chosen = selectedIdsFor(selection, group.id);
                    const single = group.maxSelect <= 1;
                    // Doar alegerile inca disponibile completeaza grupul — ca la
                    // `selectionBlockers`: o garnitura epuizata intre timp nu mai arata „1 alese".
                    const chosenAvailable = chosen.filter((optionId) =>
                      group.options.some((option) => option.id === optionId && option.available),
                    );
                    const remaining = Math.max(0, group.minSelect - chosenAvailable.length);
                    const complete = remaining === 0;
                    const reachedMaximum = !single && chosen.length >= group.maxSelect;
                    return (
                      <fieldset
                        key={group.id}
                        ref={(element) => {
                          if (element === null) groupRefs.current.delete(group.id);
                          else groupRefs.current.set(group.id, element);
                        }}
                        className="flex scroll-mt-16 flex-col gap-2"
                      >
                        <legend className="w-full">
                          <span className="flex w-full items-start justify-between gap-3">
                            <span>
                              <span className="block text-base font-bold text-ink">{group.name}</span>
                              <span className="mt-0.5 block text-xs font-normal text-ink-muted">
                                {single
                                  ? group.required
                                    ? "Alege o opțiune"
                                    : "Alege cel mult o opțiune"
                                  : `Alege ${group.minSelect > 0 ? `${group.minSelect}–` : "până la "}${group.maxSelect}`}
                              </span>
                            </span>
                            <span
                              key={nudge?.groupId === group.id ? `nudge-${nudge.n}` : "badge"}
                              aria-live="polite"
                              className={[
                                "shrink-0 rounded-full px-2.5 py-1 text-xs font-bold",
                                !complete && nudge?.groupId === group.id ? "da-nudge" : "",
                                complete ? "bg-brand-soft text-brand-on-surface" : "bg-danger-soft text-danger",
                              ].join(" ")}
                            >
                              {complete ? (chosen.length === 0 ? "Opțional" : `${chosen.length} alese`) : group.required && remaining === group.minSelect ? "Obligatoriu" : `Mai alege ${remaining}`}
                            </span>
                          </span>
                        </legend>
                        <div className="flex flex-col gap-2">
                          {group.required || !single ? null : (
                            <ChoiceRow
                              type="radio"
                              name={`${offer.offerId}-${group.id}`}
                              value=""
                              checked={chosen.length === 0}
                              onChange={() => setSelection((current) => ({ ...current, [group.id]: [] }))}
                              title="Fără extra"
                            />
                          )}
                          {group.options.map((option) => {
                            const selected = chosen.includes(option.id);
                            const keepsRoute = optionKeepsFulfillmentPossible(options.variants, selection, group.id, option.id, group.maxSelect);
                            const nextSelection = selectionAfterOptionToggle(selection, group.id, option.id, group.maxSelect);
                            const keepsPrice = selectionHasFinanciallyValidCompletion(options.groups, options.variants, nextSelection, offerCustomerUnitPrice(offer));
                            const disabled = !option.available || (!selected && (reachedMaximum || !keepsRoute || !keepsPrice));
                            const note = !option.available
                              ? "epuizat"
                              : option.available && !selected && reachedMaximum
                                ? "limită atinsă"
                                : option.available && !selected && !reachedMaximum && !keepsRoute
                                  ? "nu se combină"
                                  : null;
                            return (
                              <ChoiceRow
                                key={option.id}
                                type={single ? "radio" : "checkbox"}
                                name={`${offer.offerId}-${group.id}`}
                                value={option.id}
                                disabled={disabled}
                                checked={selected}
                                onChange={() => toggleOption(group.id, option.id, group.maxSelect)}
                                title={option.name}
                                description={
                                  option.allergens.length === 0 && nutritionRows(option.nutrition).length === 0 ? undefined : (
                                    <>
                                      {option.allergens.length === 0 ? null : <span className="text-warning">Alergeni: {option.allergens.join(", ")}</span>}
                                      {nutritionRows(option.nutrition).length === 0 ? null : (
                                        <span className="block">Valori nutriționale declarate {nutritionBasisLabel(option.nutrition)}</span>
                                      )}
                                    </>
                                  )
                                }
                                trailing={
                                  <span className="flex shrink-0 items-center gap-2 text-sm">
                                    {note === null ? null : <span className="text-xs text-ink-muted">{note}</span>}
                                    {option.priceDelta === 0 ? null : (
                                      <span className={selected ? "font-bold text-brand-on-surface" : "text-ink-muted"}>
                                        {option.priceDelta > 0 ? "+" : "−"}
                                        <Price amount={Math.abs(option.priceDelta)} />
                                      </span>
                                    )}
                                  </span>
                                }
                              />
                            );
                          })}
                        </div>
                      </fieldset>
                    );
                  })}

              {selectedAddedAllergens.length === 0 ? null : (
                <div role="status" className="rounded-card border border-warning bg-surface p-3 text-sm">
                  <p className="font-bold text-ink">Alegerile tale adaugă alergeni</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedAddedAllergens.map((allergen) => (
                      <Badge key={`selected-${allergen}`} tone="warning">{allergen}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {!showBlockers || blockers.length === 0 ? null : (
                <ul ref={blockersRef} role="alert" className="scroll-mt-16 list-disc pl-5 text-sm font-medium text-danger">
                  {blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              )}

              {unavailable || !orderable ? null : notesOpen ? (
                <TextArea
                  label="Mențiuni pentru bucătărie"
                  hint="Ex.: fără ceapă, sosul separat. Nu garantăm modificări de rețetă; pentru alergii severe sună restaurantul."
                  rows={2}
                  maxLength={300}
                  autoFocus
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setNotesOpen(true)}
                  className="self-start text-sm font-semibold text-brand-on-surface underline"
                >
                  + Adaugă o mențiune pentru bucătărie
                </button>
              )}

              {offer.allergens.length === 0 ? null : (
                <section className="rounded-card border border-warning bg-surface p-3" aria-labelledby={`${offer.offerId}-alergeni`}>
                  <div className="flex items-center gap-2">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-warning text-white" aria-hidden="true">
                      <IconWarning size={15} />
                    </span>
                    <h3 id={`${offer.offerId}-alergeni`} className="font-bold text-ink">Alergeni declarați</h3>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {offer.allergens.map((allergen) => (
                      <Badge key={allergen} tone="warning">{allergen}</Badge>
                    ))}
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-ink-muted">
                    Dacă ai o alergie severă, contactează restaurantul înainte de comandă. În bucătărie poate exista contaminare încrucișată.
                  </p>
                </section>
              )}

              {declaredNutrition.length === 0 ? null : (
                <details className="rounded-card border border-line bg-surface">
                  <summary className="da-tap cursor-pointer px-4 py-3 text-sm font-semibold text-ink">
                    Valori nutriționale
                    <span className="ml-1 text-sm font-normal text-ink-muted">{nutritionBasisLabel(offer.nutrition)}</span>
                  </summary>
                  <dl className="border-t border-line px-4 py-2 text-sm">
                    {declaredNutrition.map((row) => (
                      <div key={row.label} className="flex justify-between gap-4 border-b border-line py-2 last:border-0">
                        <dt className="text-ink-muted">{row.label}</dt>
                        <dd className="font-semibold text-ink">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}

              {selectedNutritionDeclarations.length === 0 ? null : (
                <section className="rounded-card border border-line bg-surface p-3" aria-label="Valori nutriționale extra-uri">
                  <h3 className="text-sm font-bold text-ink">Aport declarat de extra-urile alese</h3>
                  <p className="mt-1 text-xs text-ink-muted">Valorile sunt afișate separat de produsul de bază; nu le însumăm când bazele sau porțiile diferă.</p>
                  <div className="mt-3 flex flex-col gap-3">
                    {selectedNutritionDeclarations.map(({ option, rows }) => (
                      <details key={`nutrition-${option.id}`} className="rounded-control border border-line px-3 py-2">
                        <summary className="cursor-pointer text-sm font-semibold text-ink">
                          {option.name} <span className="font-normal text-ink-muted">({nutritionBasisLabel(option.nutrition)})</span>
                        </summary>
                        <dl className="mt-2 text-xs">
                          {rows.map((row) => (
                            <div key={row.label} className="flex justify-between gap-4 border-t border-line py-1.5">
                              <dt className="text-ink-muted">{row.label}</dt>
                              <dd className="font-semibold text-ink">{row.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </Modal>
      )}
    </li>
  );
}
